import { useState } from 'react';
import { authApi } from '@/lib/api';

/**
 * Browser Lab Login — learn.eswift.in/lab/login
 *
 * A student reaches the lab experience through the browser by typing this URL and
 * entering their INSTITUTE CODE (must match their own institute exactly — no hint),
 * their Student ID, and password. Authenticated against the cloud; on success the
 * student lands in the standard web student portal. Every attempt is recorded for
 * the superadmin (see the Lab Logins page).
 */
export default function LabLogin() {
    const [instituteCode, setInstituteCode] = useState('');
    const [studentId, setStudentId] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const code = instituteCode.trim();
        const id = studentId.trim();
        if (!code || !id || !password) {
            setError('Enter the institute code, student ID and password.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await authApi.labLogin({ institute_code: code, email: id, password });
            // Hand the token + user to the web Lab EXE renderer (served at /lab-app),
            // which reads `labExeHandoff` to boot straight into the SAME experience
            // as the desktop Lab EXE, against the cloud.
            localStorage.setItem('labExeHandoff', JSON.stringify({ token: res.token, user: res.user }));
            // Two surfaces, two correct answers -- and the relative form was
            // only right for one of them.
            //
            // Under file:// (the lab app shipped inside the EXE) an absolute
            // path resolves against the drive root, not the app, so it has to
            // be relative there. But this page is served at /lab/login, and a
            // relative 'lab-app/index.html' resolves against /lab/ --
            // /lab/lab-app/index.html, which the SPA catch-all answers with a
            // 919-byte index.html instead of the 82KB lab app. The student
            // then lands back on the portal login, having signed in correctly
            // a moment earlier, with nothing on screen saying why.
            //
            // So each surface gets the form that works for it.
            window.location.href = window.location.protocol === 'file:'
                ? 'lab-app/index.html'
                : '/lab-app/index.html';
        } catch (err: any) {
            setError(
                err?.response?.data?.error?.message ||
                err?.message ||
                'Login failed. Check your institute code, student ID and password.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600 text-white text-2xl shadow-lg">
                        🖥️
                    </div>
                    <h1 className="text-2xl font-black text-white tracking-tight">Lab Login</h1>
                    <p className="mt-1 text-sm text-slate-300">Sign in to your institute's lab</p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="rounded-3xl bg-white/95 backdrop-blur shadow-2xl p-7 space-y-5"
                >
                    {error && (
                        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">
                            Institute Code
                        </label>
                        <input
                            type="text"
                            value={instituteCode}
                            onChange={(e) => setInstituteCode(e.target.value)}
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            placeholder="Enter your institute code"
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 tracking-wide focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">
                            Student ID
                        </label>
                        <input
                            type="text"
                            value={studentId}
                            onChange={(e) => setStudentId(e.target.value)}
                            autoComplete="username"
                            spellCheck={false}
                            placeholder="GR number, roll number, username or email"
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPw ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                placeholder="Enter your password"
                                className="w-full rounded-xl border border-slate-300 px-4 py-3 pr-16 text-sm font-semibold text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-600"
                            >
                                {showPw ? 'HIDE' : 'SHOW'}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full rounded-xl bg-indigo-600 py-3.5 text-sm font-black uppercase tracking-wider text-white shadow-lg shadow-indigo-600/30 hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                    >
                        {submitting ? 'Signing in…' : 'Enter Lab'}
                    </button>

                    <p className="text-center text-[11px] text-slate-400">
                        Your institute code must match exactly. Contact your institute if you can't sign in.
                    </p>
                </form>
            </div>
        </div>
    );
}
